import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Wallet, Save, Search, MapPin } from 'lucide-react';

interface Row {
  zoneId: number;
  zoneName: string;
  basePrice: number;
  pricePerKg: number;
  cities: number;
  amount: number;
}

export default function DelegateCommissionsPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/zone-commissions');
      const items: Row[] = res.data?.data || [];
      setRows(items);
      setDraft(Object.fromEntries(items.map((r) => [r.zoneId, String(r.amount ?? 0)])));
    } catch (e: any) {
      setMessage({ ok: false, text: e?.response?.data?.message || 'تعذر تحميل العمولات' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changed = useMemo(
    () => rows.filter((r) => Number(draft[r.zoneId] ?? 0) !== Number(r.amount ?? 0)),
    [rows, draft]
  );

  const save = async () => {
    const invalid = changed.find((r) => {
      const v = Number(draft[r.zoneId]);
      return Number.isNaN(v) || v < 0 || v > 10000;
    });
    if (invalid) {
      setMessage({ ok: false, text: `قيمة غير صحيحة في منطقة ${invalid.zoneName}` });
      return;
    }
    try {
      setSaving(true);
      const res = await api.put('/api/zone-commissions', {
        items: changed.map((r) => ({ zoneId: r.zoneId, amount: Number(draft[r.zoneId]) })),
      });
      setMessage({ ok: true, text: res.data?.message || 'تم الحفظ' });
      await load();
    } catch (e: any) {
      setMessage({ ok: false, text: e?.response?.data?.message || 'تعذر الحفظ' });
    } finally {
      setSaving(false);
    }
  };

  const visible = rows.filter((r) => !search.trim() || r.zoneName.includes(search.trim()));

  return (
    <div className="p-6 space-y-6" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-orange-100 text-orange-600"><Wallet className="w-6 h-6" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">عمولات المناديب</h1>
            <p className="text-sm text-gray-500">عمولة المندوب عن كل شحنة مسلّمة، حسب منطقة المستلم. نفس العمولة لكل المناديب، وعلى التسليم الناجح فقط.</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving || changed.length === 0} className="bg-orange-500 hover:bg-orange-600 text-white">
          <Save className="w-4 h-4 ml-2" />
          {saving ? 'جاري الحفظ...' : `حفظ التغييرات${changed.length ? ` (${changed.length})` : ''}`}
        </Button>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm ${message.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{message.text}</div>
      )}

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute right-3 top-3 text-gray-400" />
        <Input placeholder="ابحث عن منطقة" value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-gray-500">جاري التحميل...</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="p-3 text-right">المنطقة</th>
                  <th className="p-3 text-right">عدد المدن</th>
                  <th className="p-3 text-right">سعر الشحن (أساسي + كيلو)</th>
                  <th className="p-3 text-right">عمولة المندوب (جنيه)</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const dirty = Number(draft[r.zoneId] ?? 0) !== Number(r.amount ?? 0);
                  return (
                    <tr key={r.zoneId} className={`border-t ${r.cities === 0 ? 'opacity-60' : ''}`}>
                      <td className="p-3 font-medium flex items-center gap-2"><MapPin className="w-4 h-4 text-orange-500" />{r.zoneName}</td>
                      <td className="p-3">{r.cities === 0 ? <span className="text-gray-400">لا توجد مدن</span> : r.cities}</td>
                      <td className="p-3 text-gray-600">{Number(r.basePrice).toFixed(2)} + {Number(r.pricePerKg).toFixed(2)}/كجم</td>
                      <td className="p-3">
                        <Input
                          type="number"
                          min={0}
                          step="0.5"
                          value={draft[r.zoneId] ?? '0'}
                          onChange={(e) => setDraft({ ...draft, [r.zoneId]: e.target.value })}
                          className={`w-32 ${dirty ? 'border-orange-400 ring-1 ring-orange-200' : ''}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
